// press-hold.ts — "press-hold a sheet ability button → open item details" gesture system, carved out of
// template.js's integration tail (Phase 2h of plans/template-js-carve-up.md) and TS-ported for Phase 2a of
// plans/bundler-script-load-order-phase2.md — the second Wave-2a fold into the post-template-tail aggregate.
//
// Behavior port of includes/js/press-hold-init.js: a ~450ms pointer hold on any element marked with
// data-ability_id (generated ability buttons, AOE buttons, hand-authored core/movement/weapons buttons,
// status-effect buttons, .ft-icon-map spans) opens the item info panel via openItemInfo; on release the
// trailing click is swallowed (capture phase) so the underlying useAbility / AOE start does not also fire.
// UUID-less buttons fall back to a floating label.
//
// MODULE-SCOPE SAFE: at eval this file only defines consts/functions and attaches its window globals — it
// does NOT call setupSheetAbilityPressForInfo() itself (template.js's initializeSelectionHandlers does that
// at runtime), and its only template.js dependency (openItemInfo) is referenced inside _ftPressHoldFire
// behind a `typeof` guard. No module-scope template.js reads, no eval-time DOM access → clean 2a fold.
//
// PUBLIC GLOBALS (window-attached at the bottom — PARITY-CRITICAL, see the note there):
//   • setupSheetAbilityPressForInfo — called by template.js initializeSelectionHandlers (+ core-helpers /
//     ability_button_renderer / encounter-overlays use-ability).
//   • _ftPressHoldHideLabel — consumed CROSS-FILE by side-panels-init.js (guarded `typeof … === 'function'`
//     bare-global call). It was an implicit global as a classic-script top-level function; the module must
//     re-attach it or side-panels-init silently stops hiding the press-hold label.
// Everything else (the other _ftPressHold* handlers/state, FT_PRESS_HOLD_* consts) has NO cross-file
// consumer (grep-verified) and stays module-private.

const FT_PRESS_HOLD_MS = 450;
const FT_PRESS_HOLD_MOVE_TOLERANCE_PX = 10;
// Any element inside a sheet "core abilities / attacks" group is a press-hold target so that
// hand-authored buttons without data-ability_id (e.g. add_ap, add_reaction, reduce_attack,
// add_attack, reduce_head_attack, add_head_attack, haven-return shortcuts) still get the tooltip affordance mobile users used to
// miss. The UUID-carrying buttons always match via the first two selectors regardless of where
// they are rendered (generated ability buttons, AOE buttons, status-effect buttons, etc.).
const FT_PRESS_HOLD_SELECTOR = [
    '[data-ability_id]',
    '[data-ft-info-item-id]',
    '.ft-core-abilities button',
    '.ft-core-ability-group button',
    '.ft-core-attacks button',
    '.ft-core-attack-group button',
    // icon_map() / iconMap() wrapper spans. UUID variants also carry
    // data-ft-info-item-id (matched above); named variants fall through to
    // the label fallback in _ftPressHoldFire via data-bs-original-title.
    '.ft-icon-map',
].join(', ');

interface PressHoldState {
    btn: HTMLElement;
    pointerId: number;
    pointerType: string;
    startX: number;
    startY: number;
    targetBounds: { left: number; top: number; right: number; bottom: number };
    openedInfo: boolean;
    timer: number | null;
}

let _ftPressHoldInstalled = false;
let _ftPressHoldState: PressHoldState | null = null;
let _ftPressHoldLabelEl: HTMLElement | null = null;
let _ftPressHoldLabelHideTimer: number | null = null;
let _ftPressHoldLastFireAt = 0;
const _ftPressHoldSuppressClick = new WeakSet<HTMLElement>();

// RELEASE-anchored click swallow. The _ftPressHoldSuppressClick WeakSet above self-expires 1500ms
// after the hold FIRES, so a long read-hold (open the info, keep holding while you read, THEN release)
// lost its suppression and the trailing release-click fired useAbility/use... — the "release over the
// button uses the ability" bug. Anchor the swallow to the RELEASE instead: pointerup after a fire arms
// a short window that eats the ONE imminent trailing click, no matter how long the hold lasted. Works
// for mouse (click is synchronous on mouseup) and touch/pen (the synthetic click lands within the
// grace). A fresh gesture's pointerdown clears any stale window so a deliberate tap is never eaten.
const FT_PRESS_HOLD_CLICK_SWALLOW_MS = 700;
let _ftPressHoldFired = false;       // did THIS gesture open info (so its release-click must be eaten)?
let _ftPressHoldSwallowUntil = 0;    // timestamp until which the next click is swallowed (one-shot)

function setupSheetAbilityPressForInfo(): void {
    if (_ftPressHoldInstalled) return;
    _ftPressHoldInstalled = true;

    document.addEventListener('pointerdown', _ftPressHoldPointerDown, true);
    document.addEventListener('pointermove', _ftPressHoldPointerMove, true);
    document.addEventListener('pointerup', _ftPressHoldPointerEnd, true);
    document.addEventListener('pointercancel', _ftPressHoldPointerEnd, true);
    document.addEventListener('click', _ftPressHoldClickBlocker, true);
    // Listen at both document and window in capture phase so a rogue handler can't escape via
    // stopPropagation before we get a chance to cancel the native menu.
    document.addEventListener('contextmenu', _ftPressHoldContextMenu, true);
    window.addEventListener('contextmenu', _ftPressHoldContextMenu, true);
    document.addEventListener('scroll', _ftPressHoldPointerEnd, true);

    // CSS defenses: kill the native WebKit long-press callout and text-select box that can fire
    // on iOS/Chrome mobile emulation. Injected once; these declarations are inert on desktop
    // browsers that don't recognize -webkit-touch-callout.
    if (!document.getElementById('ft-press-hold-style')) {
        const style = document.createElement('style');
        style.id = 'ft-press-hold-style';
        style.textContent = `
            ${FT_PRESS_HOLD_SELECTOR} {
                -webkit-touch-callout: none;
                -webkit-user-select: none;
                -ms-user-select: none;
                user-select: none;
            }
            ${FT_PRESS_HOLD_SELECTOR.split(',').map((s) => s.trim() + ' *').join(', ')} {
                -webkit-touch-callout: none;
                -webkit-user-select: none;
            }
        `;
        document.head.appendChild(style);
    }
}

function _ftPressHoldPointerDown(e: PointerEvent): void {
    if (!e.isPrimary) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;

    // A new gesture begins → drop any stale release-swallow window (and fired flag) so THIS tap's
    // own click is never eaten. The previous gesture's trailing click, if any, already fired before
    // this pointerdown, so clearing here only ever discards a window that has nothing left to catch.
    _ftPressHoldSwallowUntil = 0;
    _ftPressHoldFired = false;

    let btn = (e.target as Element).closest<HTMLElement>(FT_PRESS_HOLD_SELECTOR);
    if (!btn) return;

    // Outermost wins: if the matched element is a .ft-icon-map nested inside
    // another press-hold target (e.g. an equipment-slot icon inside an AOE
    // ability button), re-resolve to the outer target so the outer button
    // wins the gesture. Standalone icon_map spans are unaffected.
    if (btn.classList.contains('ft-icon-map') && btn.parentElement) {
        const outer = btn.parentElement.closest<HTMLElement>(FT_PRESS_HOLD_SELECTOR);
        if (outer && outer !== btn) btn = outer;
    }

    // Clickable-wrapper deferral: if the press-hold target is a .ft-icon-map
    // sitting inside a wrapper that owns a click action (<a>, <button>,
    // [onclick], [role="button"]), do NOT engage press-hold. The wrapper
    // already represents the user's intent — e.g. <button onclick="openEditInventoryForm(...)">
    // wrapping an icon_map('edit_inventory') span. Engaging press-hold here
    // would (a) overlay a redundant "Edit Inventory" floating label, and
    // (b) suppress the wrapper's click via _ftPressHoldClickBlocker, so the
    // user's tap silently does nothing (see FINAL TOOLTIP SYSTEM pointerdown dispose for Bootstrap).
    if (btn.classList.contains('ft-icon-map') && btn.parentElement) {
        const clickableWrapper = btn.parentElement.closest('a, button, [onclick], [role="button"]');
        if (clickableWrapper) return;
    }

    _ftPressHoldClearTimer();

    const state: PressHoldState = {
        btn,
        pointerId: e.pointerId,
        pointerType: e.pointerType,
        startX: e.clientX,
        startY: e.clientY,
        // Keep the press-start hit area. The action bar can begin its own drag after this hold has
        // opened Workspace info, and the dragged source may subsequently be repainted or detached.
        // The original icon is still the zone the pointer must leave to dismiss that accidental info.
        targetBounds: (() => {
            const rect = btn.getBoundingClientRect();
            return { left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom };
        })(),
        openedInfo: false,
        timer: null,
    };
    state.timer = setTimeout(() => _ftPressHoldFire(state), FT_PRESS_HOLD_MS);
    _ftPressHoldState = state;
}

function _ftPressHoldPointerMove(e: PointerEvent): void {
    const state = _ftPressHoldState;
    if (!state || e.pointerId !== state.pointerId) return;

    if (state.openedInfo) {
        const bounds = state.targetBounds;
        const outsideTarget = e.clientX < bounds.left || e.clientX > bounds.right
            || e.clientY < bounds.top || e.clientY > bounds.bottom;
        if (outsideTarget) {
            _ftPressHoldDismissFt3dWorkspaceInfo();
            // The hold already fired, so its eventual release still needs the ordinary trailing-click
            // swallow. Only retire its movement tracking; never reset _ftPressHoldFired here.
            _ftPressHoldClearTimer();
        }
        return;
    }

    const dx = e.clientX - state.startX;
    const dy = e.clientY - state.startY;
    if ((dx * dx + dy * dy) > (FT_PRESS_HOLD_MOVE_TOLERANCE_PX * FT_PRESS_HOLD_MOVE_TOLERANCE_PX)) {
        _ftPressHoldClearTimer();
    }
}

function _ftPressHoldPointerEnd(e?: Event): void {
    const state = _ftPressHoldState;
    if (state && (!e || (e as PointerEvent).pointerId === state.pointerId)) {
        _ftPressHoldClearTimer();
    }
    // If THIS gesture opened info, the trailing click (mouse: synchronous on mouseup; touch/pen:
    // a synthetic click ~0–350ms later) must NOT also fire useAbility. Arm a short RELEASE-anchored
    // window so the click blocker eats that one click regardless of how long the hold lasted. Only on
    // a real pointer release (pointerup/pointercancel) — NOT scroll, which would consume the fired
    // flag too early and leave the eventual real release unguarded.
    const isRelease = !e || e.type === 'pointerup' || e.type === 'pointercancel';
    if (_ftPressHoldFired && isRelease) {
        _ftPressHoldFired = false;
        _ftPressHoldSwallowUntil = Date.now() + FT_PRESS_HOLD_CLICK_SWALLOW_MS;
    }
    _ftPressHoldScheduleLabelHide();
}

function _ftPressHoldClearTimer(): void {
    if (_ftPressHoldState) {
        clearTimeout(_ftPressHoldState.timer ?? undefined);
        _ftPressHoldState = null;
    }
}

function _ftPressHoldFire(state: PressHoldState): void {
    if (_ftPressHoldState !== state) return;
    if (state.timer !== null) clearTimeout(state.timer);
    state.timer = null;
    _ftPressHoldLastFireAt = Date.now();
    _ftPressHoldFired = true; // armed at release (_ftPressHoldPointerEnd) → eats the trailing click

    const btn = state.btn;
    _ftPressHoldSuppressClick.add(btn);
    // Safety: if no synthetic click lands (e.g. user never releases over the button), expire suppression.
    setTimeout(() => _ftPressHoldSuppressClick.delete(btn), 1500);

    const infoId = btn.dataset.ability_id || btn.dataset.ftInfoItemId || '';
    if (infoId && typeof openItemInfo === 'function') {
        // Retain this exact gesture until release/movement. In FT3D the action-bar drag begins after
        // the same hold, so movement outside the icon must be able to close the Workspace info that
        // this timer just opened.
        state.openedInfo = true;
        // add_search=TRUE: a hold is how the FT3D action bar opens an ability/weapon in the Workspace,
        // so it is a real information entry and belongs in the Back/Forward timeline. The legacy
        // `false` (a drawer "peek" that left history alone) is why holding Claw then Bite left both
        // arrows dead — no entry was ever recorded (owner, 2026-09-01).
        openItemInfo(infoId, true);
        return;
    }

    _ftPressHoldState = null;
    const label = _ftPressHoldResolveLabel(btn);
    if (label) _ftPressHoldShowLabel(btn, label);
}

/**
 * FT3D portals item info into the Workspace window. A deliberate stationary hold may inspect an icon,
 * but once that same pointer leaves the icon it has become a drag gesture; retire the transient window
 * so it does not cover the play surface. Other routes keep their existing side-panel behavior.
 */
function _ftPressHoldDismissFt3dWorkspaceInfo(): void {
    if (!document.documentElement.classList.contains('ft3d-hud-active')) return;
    const workspaceMount = document.querySelector<HTMLElement>(
        '[data-ft3d-workspace-mount][data-ft3d-workspace-surface="info"]',
    );
    if (!workspaceMount) return;
    const workspace = workspaceMount.closest<HTMLElement>('[data-ft3d-window="workspace"]');
    if (!workspace || workspace.hidden) return;
    workspace.querySelector<HTMLButtonElement>('[data-ft3d-window-close="workspace"]')?.click();
}

function _ftPressHoldClickBlocker(e: MouseEvent): void {
    // RELEASE-anchored swallow: a hold just fired and released → eat the single trailing click (the one
    // that would otherwise run useAbility), even on a long hold where the WeakSet entry already expired
    // or the suppressed button node was re-rendered out from under us. One-shot (disarms immediately).
    if (_ftPressHoldSwallowUntil && Date.now() < _ftPressHoldSwallowUntil) {
        _ftPressHoldSwallowUntil = 0;
        e.stopImmediatePropagation();
        e.preventDefault();
        return;
    }
    const btn = (e.target as Element).closest<HTMLElement>(FT_PRESS_HOLD_SELECTOR);
    if (!btn) return;
    if (!_ftPressHoldSuppressClick.has(btn)) return;

    _ftPressHoldSuppressClick.delete(btn);
    e.stopImmediatePropagation();
    e.preventDefault();
}

function _ftPressHoldContextMenu(e: Event): boolean | undefined {
    // Always swallow the browser's native context menu on elements that participate in our
    // press-hold system — we own the long-press gesture. Catches both real mobile (Android
    // fires contextmenu mid-hold; iOS fires a callout) and Chrome DevTools touch emulation
    // (where a mouse long-press still reports pointerType:'mouse' but also synthesises a
    // contextmenu on release). Desktop users can still right-click anywhere off the button
    // if they need "Inspect Element".
    const tgt = e.target as Element | null;
    const inPressHoldTarget = tgt && typeof tgt.closest === 'function' && tgt.closest(FT_PRESS_HOLD_SELECTOR);
    // Also swallow if a press-hold just fired anywhere — on release the contextmenu target can
    // be retargeted to body/html by the browser if the originating button lost focus, so the
    // closest() check alone is not enough.
    const pressHoldJustFired =
        (_ftPressHoldLabelEl !== null) ||
        (_ftPressHoldState !== null) ||
        (_ftPressHoldLastFireAt && (Date.now() - _ftPressHoldLastFireAt) < 2000);
    if (inPressHoldTarget || pressHoldJustFired) {
        e.preventDefault();
        e.stopImmediatePropagation();
        return false;
    }
    return undefined;
}

function _ftPressHoldResolveLabel(btn: HTMLElement): string {
    const direct = (btn.getAttribute('title') || '').trim();
    if (direct) return direct;
    const tipHost = btn.querySelector('[data-bs-original-title], [data-bs-toggle="tooltip"]');
    if (tipHost) {
        const t = (tipHost.getAttribute('data-bs-original-title') || tipHost.getAttribute('title') || '').trim();
        if (t) return t;
    }
    const img = btn.querySelector('img[alt]');
    if (img) return (img.getAttribute('alt') || '').trim();
    return '';
}

function _ftPressHoldShowLabel(btn: HTMLElement, text: string): void {
    _ftPressHoldHideLabel();

    const el = document.createElement('div');
    el.className = 'ft-press-hold-label shadow-sm rounded bg-dark text-light px-2 py-1 small';
    el.style.position = 'fixed';
    el.style.zIndex = '30000';
    el.style.pointerEvents = 'none';
    el.style.maxWidth = '70vw';
    el.style.whiteSpace = 'normal';
    el.style.textAlign = 'center';
    el.textContent = text;
    document.body.appendChild(el);

    const rect = btn.getBoundingClientRect();
    const labelW = el.offsetWidth;
    const labelH = el.offsetHeight;
    let top = rect.top - 8 - labelH;
    if (top < 8) top = rect.bottom + 8;
    let left = rect.left + (rect.width / 2) - (labelW / 2);
    left = Math.max(8, Math.min(left, window.innerWidth - labelW - 8));
    el.style.top = `${top}px`;
    el.style.left = `${left}px`;

    _ftPressHoldLabelEl = el;
    // Auto-hide after a short dwell so it behaves like a brief tooltip rather than a sticky UI.
    _ftPressHoldScheduleLabelHide(2000);
}

function _ftPressHoldScheduleLabelHide(delayMs = 600): void {
    if (!_ftPressHoldLabelEl) return;
    if (_ftPressHoldLabelHideTimer) clearTimeout(_ftPressHoldLabelHideTimer);
    _ftPressHoldLabelHideTimer = setTimeout(_ftPressHoldHideLabel, delayMs);
}

function _ftPressHoldHideLabel(): void {
    if (_ftPressHoldLabelHideTimer) {
        clearTimeout(_ftPressHoldLabelHideTimer);
        _ftPressHoldLabelHideTimer = null;
    }
    if (_ftPressHoldLabelEl) {
        _ftPressHoldLabelEl.remove();
        _ftPressHoldLabelEl = null;
    }
}

// ===========================================================================================
// Window attachment — PARITY-CRITICAL. The originals were implicit globals (classic-script top-level
// function declarations). `setupSheetAbilityPressForInfo` is called by template.js / core-helpers /
// ability_button_renderer / encounter-overlays. `_ftPressHoldHideLabel` is called cross-file by
// side-panels-init.js (bare-global `typeof … === 'function'` guard) — it MUST stay attached or that hide
// silently no-ops. The other _ftPressHold* internals have no cross-file consumer (grep-verified) and are
// intentionally kept module-private.
// ===========================================================================================
declare global {
    interface Window {
        setupSheetAbilityPressForInfo: typeof setupSheetAbilityPressForInfo;
        _ftPressHoldHideLabel: typeof _ftPressHoldHideLabel;
    }
}

Object.assign(window, { setupSheetAbilityPressForInfo, _ftPressHoldHideLabel });

export {};
